import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import CheerCard from '../../components/common/CheerCard';
import { PurposePills } from '../../components/common/EventTags';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './SendCheer.css';

/* What colleagues said about you, and what you said about them. F7.

   ★ **The whole screen exists for one decision.** A cheer arrives pending and only the person it
   is about can answer it, so the waiting ones are at the top with two buttons and everything else
   is below them. The two buttons are deliberately NOT "accept / reject": declining is *keeping it
   to yourself*, because somebody who would rather not be praised in public must be able to say so
   without it reading as a refusal of the colleague who wrote it. Nothing rings on a decline.

   ★ The chip tally above the collected cheers is the summary a list of paragraphs cannot make:
   three cheers all naming 'Win Together' is a fact about somebody. */

const CheersForMe = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/recognition/inbox')
      .then(setData)
      .catch((e) => setError(apiErrorText(t, e, 'somethingWentWrong')));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, accepting) => {
    setBusy(id);
    setError(null);
    try {
      await apiRequest('POST', `/recognition/${id}/${accepting ? 'accept' : 'decline'}`);
      load();
    } catch (e) {
      setError(apiErrorText(t, e, 'somethingWentWrong'));
    } finally {
      setBusy(null);
    }
  };

  const pending = data?.pending || [];
  const received = data?.received || [];
  const given = data?.given || [];
  const chips = data?.chips || [];

  return (
    <Screen>
      <Back to="/member-home" />
      <div className="top">
        <Title>{t('cheersForMeTitle')}</Title>

        {error && <p className="bravos-cal__notice mx-3">{error}</p>}
        {!data && !error && <span className="auth__spinner" />}

        {/* Waiting for you — the reason this screen exists, so it is first. */}
        {pending.length > 0 && (
          <Card>
            <Title my="1">{t('cheerWaitingTitle')}</Title>
            <p className="ce-note">{t('cheerWaitingNote')}</p>
            {pending.map((cheer) => (
              <CheerCard key={cheer.id} cheer={cheer} className="cc--pending">
                <div className="cc-acts">
                  <Button theme="primary" mb="0" disabled={busy === cheer.id}
                          onClick={() => decide(cheer.id, true)}>
                    {t('cheerShowIt')}
                  </Button>
                  <Button theme="light" mb="0" disabled={busy === cheer.id}
                          onClick={() => decide(cheer.id, false)}>
                    {t('cheerKeepIt')}
                  </Button>
                </div>
              </CheerCard>
            ))}
          </Card>
        )}

        {/* What you are recognised for — the tally, then the sentences behind it. */}
        <Card>
          <Title my="1">{t('cheerCollectedTitle')}</Title>
          {chips.length > 0 && (
            <div className="sc-tally">
              {chips.map(({ chip, n }) => (
                <span className="sc-tally__row" key={`${chip.kind}-${chip.id}`}>
                  <PurposePills items={[chip]} />
                  <span className="sc-tally__n">×{n}</span>
                </span>
              ))}
            </div>
          )}
          {received.length === 0
            ? <p className="ce-note">{t('cheerNoneYet')}</p>
            : received.map((cheer) => <CheerCard key={cheer.id} cheer={cheer} />)}
        </Card>

        {/* What you sent, in every state, so a sender can see theirs landed. */}
        <Card>
          <Title my="1">{t('cheerGivenTitle')}</Title>
          {given.length === 0
            ? <p className="ce-note">{t('cheerGivenNone')}</p>
            : given.map((cheer) => (
              <CheerCard key={cheer.id} cheer={cheer}>
                <span className={`cc-state cc-state--${cheer.status}`}>
                  {t(`cheerState_${cheer.status}`)}
                </span>
              </CheerCard>
            ))}
        </Card>
      </div>

      <div className="bottom">
        <div className="px-3">
          <Button theme="primary" onClick={() => navigate('/cheer')}>
            <Icon name="hands-clapping" className="me-2" />{t('cheerTitle')}
          </Button>
        </div>
      </div>
    </Screen>
  );
};

export default CheersForMe;
